// File: frontend/src/pages/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from '@azure/msal-browser';
import useSWRInfinite from 'swr/infinite';
import { useApi } from '../hooks/useApi';
import { useDebounce } from 'use-debounce';
import { 
  Link, 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent, 
  CardActions, 
  TextField, 
  InputAdornment, 
  Paper,
  Chip,
  Stack,
  styled,
  alpha,
  Fade,
  Divider,
  Button
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArticleIcon from '@mui/icons-material/Article';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReportFilters from './ReportFilters';
import { getTagColor } from '../utils/tagColors';
import { cyber } from '../theme';

import { Report } from '../types';

const PAGE_SIZE = 12;

// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${cyber.bgPaper} 0%, #3a2d6e 55%, ${cyber.primary} 130%)`,
  border: `1px solid ${alpha(cyber.primary, 0.35)}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '300px',
    height: '300px',
    background: alpha(cyber.primaryLight, 0.25),
    borderRadius: '50%',
    filter: 'blur(80px)',
  }
}));

const AIBadge = styled(Chip)(({ theme }) => ({
  background: alpha(cyber.primary, 0.15),
  border: `1px solid ${alpha(cyber.primary, 0.4)}`,
  color: cyber.primaryLight,
  fontWeight: 700,
  fontSize: '0.65rem',
  height: '20px',
  '& .MuiChip-icon': {
    color: cyber.primaryLight,
    fontSize: '0.9rem',
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.25s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-3px)',
    borderColor: alpha(cyber.primary, 0.6),
    boxShadow: cyber.glow,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: cyber.gradient,
    opacity: 0,
    transition: 'opacity 0.25s ease',
  },
  '&:hover::before': {
    opacity: 1,
  }
}));

const SearchPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const StatsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  marginTop: theme.spacing(2),
  flexWrap: 'wrap',
}));

const StatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  background: 'rgba(255, 255, 255, 0.15)',
  borderRadius: theme.spacing(1),
  backdropFilter: 'blur(10px)',
}));

const NewBadge = styled(Chip)(({ theme }) => ({
  background: alpha(cyber.accent.cyan, 0.15),
  border: `1px solid ${alpha(cyber.accent.cyan, 0.5)}`,
  color: cyber.accent.cyan,
  fontWeight: 700,
  fontSize: '0.65rem',
  height: '20px',
  letterSpacing: '0.05em',
}));

// Reports published within the last 7 days get the NEW badge
const isRecentReport = (publishedDate: string) =>
  Date.now() - new Date(publishedDate).getTime() < 7 * 24 * 60 * 60 * 1000;

const Dashboard = () => {
    const { accounts, inProgress } = useMsal();
    const { authenticatedFetcher } = useApi();
    const [searchParams, setSearchParams] = useSearchParams();

    // Initialize search term from URL query parameter
    const initialSearch = searchParams.get('search') || '';
    const [searchTerm, setSearchTerm] = useState(initialSearch);
    const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
    const [source, setSource] = useState('');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [timeRange, setTimeRange] = useState('');

    // Clear URL param after initial load (only once on mount)
    useEffect(() => {
        const currentSearch = searchParams.get('search');
        if (currentSearch) {
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const userName = accounts.length > 0 ? accounts[0].name : "Unknown user";

    const getKey = (pageIndex: number, previousPageData: any) => {
        if (inProgress !== InteractionStatus.None) return null;
        if (previousPageData && !previousPageData.items.length) return null;
        
        const offset = pageIndex * PAGE_SIZE;
        const searchParams = new URLSearchParams({
            status: 'completed',
            limit: String(PAGE_SIZE),
            offset: String(offset),
        });
        if (debouncedSearchTerm) searchParams.append('search', debouncedSearchTerm);
        if (source) searchParams.append('source', source);
        if (timeRange) searchParams.append('time_range', timeRange);
        if (selectedTags.length > 0) searchParams.append('tags', selectedTags.join(','));
        
        return `/api/reports?${searchParams.toString()}`;
    };

    const { data, error, isLoading, size, setSize } = useSWRInfinite(
        getKey,
        authenticatedFetcher,
        { revalidateFirstPage: false }
    );

    const reports = data ? data.flatMap(page => page.items as Report[]) : [];
    const totalReports = data?.[0]?.total ?? null;
    const isLoadingMore = isLoading || (size > 0 && data && typeof data[size - 1] === "undefined");
    const isEmpty = data?.[0]?.items.length === 0;
    const isReachingEnd = isEmpty || (data && data[data.length - 1]?.items.length < PAGE_SIZE);

    useEffect(() => {
        setSize(1);
    }, [debouncedSearchTerm, source, timeRange, selectedTags, setSize]);

    return (
        <Box>
            {/* Hero Section */}
            <HeroSection>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <PsychologyIcon sx={{ fontSize: 36 }} />
                        <Typography variant="h4" component="h1" fontWeight="bold">
                            AI-Powered Threat Intelligence
                        </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ mb: 1.5, opacity: 0.95 }}>
                        Welcome back, {userName}! 👋
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, maxWidth: '800px', opacity: 0.9 }}>
                        Explore the latest cyber threat reports, automatically analyzed and enriched by our advanced AI system. Get instant insights, IOC extraction, and actionable intelligence.
                    </Typography>
                    <StatsBox>
                        <StatItem>
                            <AutoAwesomeIcon sx={{ color: cyber.accent.coral }} />
                            <Typography variant="body2" fontWeight="bold">
                                AI-Generated Summaries
                            </Typography>
                        </StatItem>
                        <StatItem>
                            <TrendingUpIcon sx={{ color: cyber.accent.green }} />
                            <Typography variant="body2" fontWeight="bold">
                                Real-Time Updates
                            </Typography>
                        </StatItem>
                        <StatItem>
                            <ArticleIcon sx={{ color: cyber.accent.cyan }} />
                            <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: totalReports !== null ? cyber.fontMono : undefined }}>
                                {totalReports !== null ? `${totalReports.toLocaleString()} Reports Available` : 'Loading Reports...'}
                            </Typography>
                        </StatItem>
                    </StatsBox>
                </Box>
            </HeroSection>
            
            {/* Search & Filters */}
            <SearchPaper elevation={0}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                        <TextField 
                            fullWidth 
                            variant="outlined" 
                            size="medium"
                            placeholder="Search by title, source, summary, CVE, IOC or keyword..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="primary" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        />
                    </Box>
                    <ReportFilters
                        source={source}
                        onSourceChange={setSource}
                        selectedTags={selectedTags}
                        onSelectedTagsChange={setSelectedTags}
                        timeRange={timeRange}
                        onTimeRangeChange={setTimeRange}
                    />
                </Box>
            </SearchPaper>

            {/* Loading State */}
            {(isLoading && !reports.length) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', my: 8 }}>
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                        Loading AI-analyzed reports...
                    </Typography>
                </Box>
            )}
            
            {/* Error State */}
            {error && (
                <Alert severity="error" sx={{ my: 2 }}>
                    {`Failed to fetch reports: ${error.message}`}
                </Alert>
            )}
            
            {/* Reports Grid */}
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
                {reports.map((report: Report, index: number) => (
                    <Fade in timeout={300 + (index % 12) * 50} key={report.id}>
                        <StyledCard>
                            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                {/* AI Badge + recency */}
                                <Stack direction="row" spacing={0.75} sx={{ mb: 2 }}>
                                    <AIBadge
                                        icon={<AutoAwesomeIcon />}
                                        label="AI Analyzed"
                                        size="small"
                                    />
                                    {isRecentReport(report.publishedDate) && (
                                        <NewBadge label="NEW" size="small" />
                                    )}
                                </Stack>

                                {/* Title */}
                                <Typography 
                                    gutterBottom 
                                    variant="h6" 
                                    component="div"
                                    sx={{ 
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                        minHeight: '50px',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {report.title || "No Title"}
                                </Typography>
                                
                                {/* AI Summary */}
                                {report.dashboardSummary && (
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 1.5, 
                                            mb: 2, 
                                            bgcolor: alpha(cyber.primary, 0.05),
                                            border: '1px solid',
                                            borderColor: alpha(cyber.primary, 0.12),
                                            borderRadius: 1,
                                            minHeight: '80px'
                                        }}
                                    >
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ 
                                                fontStyle: 'italic',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {report.dashboardSummary}
                                        </Typography>
                                    </Paper>
                                )}
                                
                                {/* Metadata */}
                                <Stack spacing={0.5} sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <ArticleIcon sx={{ fontSize: 14, color: cyber.accent.cyan }} />
                                        <strong>Source:</strong>{' '}
                                        <Box component="span" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono, fontSize: '0.7rem' }}>
                                            {report.sourceFeedName}
                                        </Box>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        <strong>Published:</strong>{' '}
                                        <Box component="span" sx={{ fontFamily: cyber.fontMono, fontSize: '0.7rem' }}>
                                            {new Date(report.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </Box>
                                    </Typography>
                                </Stack>

                                {/* Tags */}
                                {report.tags && report.tags.length > 0 && (
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                                        {report.tags.slice(0, 3).map(tag => (
                                            <Chip
                                                key={tag}
                                                label={tag}
                                                size="small"
                                                color={getTagColor(tag)}
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: '20px' }}
                                            />
                                        ))}
                                        {report.tags.length > 3 && (
                                            <Chip
                                                label={`+${report.tags.length - 3}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: '20px' }}
                                            />
                                        )}
                                    </Stack>
                                )}
                            </CardContent>
                            
                            <Divider />
                            
                            <CardActions sx={{ p: 2, pt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                {/* Main CTA Button */}
                                <Button
                                    component={RouterLink}
                                    to={`/report/${report.id}`}
                                    startIcon={<PsychologyIcon />}
                                    sx={{ 
                                        flex: '1 1 auto',
                                        background: cyber.gradient,
                                        color: 'white',
                                        fontWeight: 'bold',
                                        padding: '10px 24px',
                                        borderRadius: 1,
                                        textTransform: 'none',
                                        fontSize: '0.95rem',
                                        boxShadow: '0 4px 12px rgba(123, 76, 255, 0.3)',
                                        transition: 'all 0.3s ease-in-out',
                                        '&:hover': {
                                            background: cyber.gradientHover,
                                            boxShadow: '0 6px 16px rgba(123, 76, 255, 0.4)',
                                        }
                                    }}
                                >
                                    View AI Analysis
                                </Button>

                                {/* Original Article Link */}
                                <Button
                                    component={Link}
                                    href={report.articleUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    size="small"
                                    variant="outlined"
                                    endIcon={<OpenInNewIcon fontSize="small" />}
                                    sx={{ 
                                        textTransform: 'none',
                                        color: 'text.secondary',
                                        borderColor: alpha(cyber.primary, 0.3),
                                        whiteSpace: 'nowrap',
                                        '&:hover': {
                                            borderColor: cyber.primary,
                                            color: 'primary.main',
                                            bgcolor: alpha(cyber.primary, 0.05)
                                        }
                                    }}
                                >
                                    Source
                                </Button>
                            </CardActions>
                        </StyledCard>
                    </Fade>
                ))}
            </Box>
            
            {/* Empty State */}
            {!isLoading && isEmpty && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <SearchIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No reports found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Try adjusting your search criteria or filters
                    </Typography>
                </Box>
            )}
            
            {/* Load More Button */}
            {!isReachingEnd && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                    <Button 
                        variant="outlined" 
                        size="large"
                        onClick={() => setSize(size + 1)} 
                        disabled={isLoadingMore}
                        sx={{ 
                            minWidth: '200px',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 'bold'
                        }}
                    >
                        {isLoadingMore ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Loading...
                            </>
                        ) : (
                            'Load More Reports'
                        )}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default Dashboard;